import { useEffect, useState } from "react";
import { Dropdown } from "../../../../components/Dropdown";
import { Flex } from "../../../../components/Flex";
import { TrashIcon } from "../../../../components/icons/TrashIcon";
import { Input } from "../../../../components/Input";
import Loader from "../../../../components/Loader";
import { Text } from "../../../../components/Text";
import { View } from "../../../../components/View";
import {
  ToggleProduct,
  useToggleProductsQuery,
} from "../../../../graphql/generated";
import useGqlClient from "../../../../hooks/useGqlClient";
import styled from "../../../../styles";

interface ToggleVoucherProps {
  toggleProductValue?: number | null;
  setToggleProductValue: (value: number | null) => void;
  toggleProductId?: number | null;
  setToggleProductId: (id: number | null) => void;
}

const ToggleVoucher = (props: ToggleVoucherProps) => {
  const client = useGqlClient();
  const { data: toggleData, isLoading, error } = useToggleProductsQuery(client);
  const {
    setToggleProductValue,
    toggleProductValue,
    toggleProductId,
    setToggleProductId,
  } = props;

  const [selectedProduct, setSelectedProduct] = useState<
    ToggleProduct | undefined
  >(() => {
    return toggleData?.toggleProducts.find((p) => p.id === toggleProductId);
  });

  useEffect(() => {
    if (!toggleData) return;

    const product = toggleData.toggleProducts.find(
      (p) => p.id === toggleProductId
    );
    setSelectedProduct(product);

    if (!product) {
      setToggleProductValue(null);
    } else if (product.priceModifiers.length === 0) {
      setToggleProductValue(null);
    } else if (!toggleProductValue) {
      setToggleProductValue(product.priceModifiers[0].value);
    }
  }, [setToggleProductValue, toggleProductId, toggleProductValue, toggleData]);

  if (isLoading) {
    return <Loader />;
  }

  if (error || !toggleData) {
    return (
      <Text size="s" margin="0" colorPreset="link" weight="semi">
        Select Toggle Voucher
      </Text>
    );
  }

  if (toggleData.toggleProducts.length === 0) {
    return null;
  }

  return (
    <ToggleWrap direction="row">
      <View margin="0 m 0 0">
        <Text margin="0 0 xs" size="s" weight="semi">
          Toggle Product
        </Text>
        <Dropdown
          width={220}
          size="s"
          selectionMode="single"
          renderLabel={() => {
            const selected = toggleData.toggleProducts.find(
              (p) => p.id === toggleProductId
            );
            return selected ? selected.name : "Select a product";
          }}
          options={
            toggleData.toggleProducts.map((product) => ({
              label: product.name,
              value: product.id.toString(),
            })) || []
          }
          selectedOptions={[toggleProductId ? toggleProductId.toString() : ""]}
          setSelectedOptions={(value) => {
            setToggleProductId(Number(value[0]));
            setSelectedProduct(
              toggleData.toggleProducts.find(
                (product) => product.id === Number(value[0])
              )
            );
          }}
        />
      </View>
      <View>
        <Text margin="0 0 xs" size="s" weight="semi">
          Voucher Value
        </Text>
        {selectedProduct?.priceModifiers &&
        selectedProduct.priceModifiers.filter((pm) => pm.value > 0).length >
          0 ? (
          <Dropdown
            size="s"
            width={150}
            selectionMode="single"
            renderLabel={() => {
              if (!toggleProductValue) {
                return "Select value";
              }

              return `£${toggleProductValue / 100}.00`;
            }}
            options={selectedProduct?.priceModifiers
              .sort((a, b) => a.value - b.value)
              .map((pm) => ({
                label: `£${pm.value / 100}.00`,
                value: pm.value.toString(),
              }))}
            selectedOptions={[
              toggleProductValue ? toggleProductValue.toString() : "",
            ]}
            disableOptionSort
            setSelectedOptions={(value) => {
              setToggleProductValue(Number(value[0]));
            }}
          />
        ) : (
          <Flex direction="row" align="center">
            <Input
              prefix="£"
              inputSize="s"
              value={toggleProductValue?.toString() || ""}
              onChange={(e) => {
                setToggleProductValue(Number(e.target.value));
              }}
            />
          </Flex>
        )}
      </View>
    </ToggleWrap>
  );
};

interface GiftVoucherProps {
  voucher: number;
  setVoucher: (voucher: number) => void;
  voucherEnabled: boolean;
  setVoucherEnabled: (voucherEnabled: boolean) => void;
}

export const GiftVoucher = (props: GiftVoucherProps) => {
  const [voucherType, setVoucherType] = useState<string>("toggle");

  if (props.voucherEnabled) {
    return (
      <Flex margin="s 0 0 0" direction="row" align="center">
        <ToggleWrap direction="row">
          <View margin="0 m 0 0">
            <Text margin="0 0 xs" size="s" weight="semi">
              Voucher Type
            </Text>
            <Dropdown
              width={180}
              size="s"
              selectionMode="single"
              renderLabel={() => {
                return voucherType === "prepaid"
                  ? "Prepaid Card (New)"
                  : "Toggle Voucher";
              }}
              options={[
                { label: "Toggle Voucher", value: "toggle" },
                { label: "Prepaid Card (New)", value: "prepaid" },
              ]}
              disableOptionSort
              selectedOptions={[voucherType]}
              setSelectedOptions={(value) => {
                setVoucherType(value[0]);
              }}
            />
          </View>
          <View margin="0 m 0 0">
            <Text margin="0 0 xs" size="s" weight="semi">
              Amount
            </Text>
            <Dropdown
              width={180}
              size="s"
              selectionMode="single"
              renderLabel={() => {
                if (!props.voucher) {
                  return "Select an amount";
                }

                return `£${props.voucher}.00`;
              }}
              options={[
                { label: "£50", value: "50" },
                { label: "£100", value: "100" },
                { label: "£200", value: "200" },
                { label: "£300", value: "300" },
              ]}
              disableOptionSort
              selectedOptions={[props.voucher.toString()]}
              setSelectedOptions={(value) => {
                props.setVoucher(Number(value[0]));
              }}
            />
          </View>
        </ToggleWrap>
        <View margin="0 0 0 m">
          <TrashWrap
            onClick={() => {
              props.setVoucherEnabled(false);
            }}
          >
            <TrashIcon width={14} />
          </TrashWrap>
        </View>
      </Flex>
    );
  }

  return (
    <Flex margin="m 0 0 0" style={{ flex: 1 }}>
      <Text
        onClick={() => props.setVoucherEnabled(true)}
        size="s"
        margin="0"
        colorPreset="link"
        weight="semi"
        style={{ marginLeft: "auto" }}
      >
        + Add Voucher
      </Text>
    </Flex>
  );
};

export const Toggle = (props: ToggleVoucherProps) => {
  const [toggleEnabled, setToggleEnabled] = useState(
    !!props.toggleProductValue && !!props.toggleProductId
  );

  useEffect(() => {
    setToggleEnabled(!!props.toggleProductValue && !!props.toggleProductId);
  }, [props.toggleProductValue, props.toggleProductId]);

  if (toggleEnabled) {
    return (
      <Flex direction="row" align="center">
        <ToggleVoucher {...props} />
        <View margin="0 0 0 m">
          <TrashWrap
            onClick={() => {
              setToggleEnabled(false);
              props.setToggleProductValue(null);
              props.setToggleProductId(null);
            }}
          >
            <TrashIcon width={14} />
          </TrashWrap>
        </View>
      </Flex>
    );
  }

  return (
    <Flex style={{ flex: 1 }}>
      <Text
        onClick={() => setToggleEnabled(true)}
        size="s"
        margin="0"
        colorPreset="link"
        weight="semi"
        style={{ marginLeft: "auto" }}
      >
        + Toggle Voucher
      </Text>
    </Flex>
  );
};

const ToggleWrap = styled(Flex)`
  background-color: ${(p) => p.theme.color.card.callout};
  border-radius: ${(p) => p.theme.misc.borderRadius};
  padding: ${(p) => p.theme.spacing.m} ${(p) => p.theme.spacing.m};
  margin: ${(p) => p.theme.spacing.s} 0 0 0;
`;

const TrashWrap = styled(Flex)`
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  svg {
    path {
      fill: ${(p) => p.theme.color.typography.secondary};
    }
  }

  &:hover {
    svg {
      path {
        fill: ${(p) => p.theme.color.typography.text};
      }
    }
  }
`;
